import moment from 'moment-timezone';
import {
  TAssignOrder, TCommissionPlan, TCommissionTeam, TLineItem, TMember, TPersonnel, TTimeCardShift, TTimesheetsList,
} from '../../types';
import { calculateOrderCommissionByPlan, filterUserOrders } from './orders.helper';

type TUser = {
  shopifyId: number,
  name: string,
  email: number | string
  commissionTags: Array<string>
};

type TCommissionsByDate = {
  [key: string]: {
    date: string;
    commission: number;
    netSales: number;
    productsCount: number;
    ordersCount: number;
    index: number;
    source: 'orders' | 'timesheets';
    lineItems: Array<TLineItem>
  };
};

const getCommissionsByDate = (
  orders: Array<TAssignOrder>,
  user: TUser,
  timesheets: Array<TTimesheetsList>,
  timezone: string,
) => {
  const userOrders = orders.filter((order) => {
    if (String(order.userId) === String(user.shopifyId)) {
      return true;
    }

    if (Array.isArray(order.tags) && Array.isArray(user.commissionTags)) {
      return order.tags.some((tag) => user.commissionTags.includes(tag));
    }

    return false;
  });

  const userTimesheets = timesheets.find(
    (timesheet) => timesheet.personnel.email === user.email,
  );

  const commissionsByDate: TCommissionsByDate = {};

  // Process orders
  userOrders.forEach((order, currentIndex) => {
    const orderDate = moment(order.processedAt).format('YYYY-MM-DD');
    const orderCommission = parseFloat(order.commission as string);
    const orderProductsCount = order.lineItems.reduce((sum, item) => sum + item.quantity, 0);

    if (!commissionsByDate[orderDate]) {
      commissionsByDate[orderDate] = {
        date: orderDate,
        commission: 0,
        netSales: 0,
        productsCount: 0,
        ordersCount: 0,
        index: currentIndex,
        source: 'orders',
        lineItems: [],
      };
    }

    const netSalesToAdd = parseFloat(order.summary.netSales.toFixed(2));

    commissionsByDate[orderDate].commission += orderCommission;
    commissionsByDate[orderDate].netSales = parseFloat(
      (commissionsByDate[orderDate].netSales + netSalesToAdd).toFixed(2),
    );
    commissionsByDate[orderDate].productsCount += orderProductsCount;
    commissionsByDate[orderDate].ordersCount += 1;
    commissionsByDate[orderDate].lineItems.push(...order.lineItems);
  });

  // Process timesheets
  if (userTimesheets) {
    userTimesheets.shifts.forEach((shift, index) => {
      const shiftDate = moment(shift.start).tz(timezone || shift.timezone).format('YYYY-MM-DD');

      if (!commissionsByDate[shiftDate]) {
        commissionsByDate[shiftDate] = {
          date: shiftDate,
          commission: 0,
          netSales: 0,
          productsCount: 0,
          ordersCount: 0,
          index: (Object.values(commissionsByDate)?.length || 0) + index,
          source: 'timesheets',
          lineItems: [],
        };
      }
    });
  }

  // Return merged data as an array
  return Object.values(commissionsByDate);
};

const getUsersSummary = (
  orders: Array<TAssignOrder>,
  members: Array<TMember>,
  commissionPlan: TCommissionPlan,
  timesheets: Array<TTimesheetsList>,
  commissionTeams: Array<TCommissionTeam>,
  timezone: string,
) => {
  const unPromotersMembers = members.filter((member) => !member.isPromoterCommission);

  const userSummaries = [] as Array<{
    user: TMember
    productsCount: number
    ordersCount: number
    totalSales: number
    totalCommissions: number
    dailyCommissions: number
    inTeam: boolean
  }>;

  orders.forEach((order) => {
    const member = unPromotersMembers.find((m) => {
      if (String(m.shopifyId) === String(order.userId)) return true;
      if (Array.isArray(m.commissionTags)) return m.commissionTags.some((tag) => order.tags.includes(tag));
      return false;
    });

    if (!member) return;

    const user = member;

    let userSummary = userSummaries.find((u) => String(u.user.email) === String(user.email));

    if (!userSummary) {
      const inTeam = commissionTeams.some((team) => team.members.some((teamMember) => teamMember.email === user.email));

      userSummary = {
        user,
        productsCount: 0,
        ordersCount: 0,
        totalSales: 0,
        totalCommissions: 0,
        dailyCommissions: 0,
        inTeam,
      };
      userSummaries.push(userSummary);
    }

    // Calculate the details for this order
    const orderProductsCount = order.lineItems.reduce((sum, item) => sum + item.quantity, 0);
    const orderCommission = parseFloat(order.commission as string);

    // Update the user's summary
    userSummary.productsCount += orderProductsCount;
    userSummary.ordersCount += 1;
    userSummary.totalSales += order.summary.netSales;
    userSummary.totalCommissions += orderCommission;

    // Calculate the commissions by date
    const commissionsByDate = getCommissionsByDate(orders, user, timesheets, timezone);

    if (commissionPlan?.id) {
      userSummary.dailyCommissions = commissionsByDate.reduce(
        (sum: number, row) => sum
          + (parseFloat(
            calculateOrderCommissionByPlan(
              row.netSales.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
              user.commissionPlan || commissionPlan,
              row.lineItems,
            )?.totalCommission?.toString() || '0',
          )),
        0,
      );
    }
  });

  // Now, process members from timesheets that are not already included in the summaries
  timesheets.forEach((timesheet) => {
    const hasShifts = timesheet.shifts.length > 0; // Ensure the timesheet has shifts

    if (hasShifts) {
      const member = unPromotersMembers.find((m) => m.email === timesheet.personnel.email);

      // Add the member with zeroed-out data if they are not already in userSummaries
      if (!userSummaries.some((u) => u.user.email === timesheet.personnel.email)) {
        userSummaries.push({
          user: (member || timesheet.personnel) as TMember,
          productsCount: 0,
          ordersCount: 0,
          totalSales: 0,
          totalCommissions: 0,
          dailyCommissions: 0,
          inTeam: false,
        });
      }
    }
  });

  return userSummaries;
};

const mapWorkTimesByDate = (data: Array<TTimeCardShift>, timezone: string) => data?.reduce((acc, entry) => {
  // Extract user identifier (email or shopifyId) and date
  const userKey = entry.email; // You can change this to entry.shopifyId if needed
  const date = moment.tz(entry.start, timezone).format('DD/MM/YYYY');

  // Ensure we have an entry for this user
  if (!acc[userKey]) {
    acc[userKey] = {}; // Initialize an object for each user
  }

  // Ensure we have an entry for this date under the user
  if (!acc[userKey][date]) {
    acc[userKey][date] = { workTimes: 0 };
  }

  // Sum up the totalHours for each shift on the same date for this user
  acc[userKey][date].workTimes += parseFloat(entry.totalHours);

  return acc;
}, {} as { [userKey: string]: { [date: string]: { workTimes: number } } });

const getOrdersByPermissions = (personnel: TPersonnel, orders: Array<TAssignOrder>) => {
  if (personnel.permissionLevel === 'Employee') {
    return filterUserOrders(orders, personnel);
  } if (personnel.permissionLevel === 'Manager') {
    const locationIds = personnel.locations.map((location) => Number(location.id));
    return orders.filter((order) => locationIds.includes(order.locationId));
  }
  return orders;
};

const summarizeByLocation = (orders: Array<TAssignOrder>) => {
  const summary: { [key: string]: {
    totalOrders: number,
    totalProducts: number,
    totalSubTotal: number,
    totalCommission: number,
  } } = {};

  orders.forEach((order) => {
    const {
      locationId, lineItems, subTotal, commission,
    } = order;
    const totalProducts = lineItems.reduce((acc, item) => acc + item.quantity, 0);

    if (!summary[locationId]) {
      summary[locationId] = {
        totalOrders: 0,
        totalProducts: 0,
        totalSubTotal: 0,
        totalCommission: 0,
      };
    }

    // Updating the totals for the locationId
    summary[locationId].totalOrders += 1;
    summary[locationId].totalProducts += totalProducts;
    summary[locationId].totalSubTotal += parseFloat(subTotal);
    summary[locationId].totalCommission += parseFloat(commission as string);
  });

  return summary;
};

const summarizeByDayAndLocation = (orders: Array<TAssignOrder>, timezone: string) => {
  const summary: {
    [day: string]: {
      [locationId: string]: {
        totalOrders: number,
        totalProducts: number,
        totalSubTotal: number,
        totalCommission: number,
      }
    }
  } = {};

  orders.forEach((order) => {
    const {
      locationId, lineItems, subTotal, commission, processedAt, // Assuming you have an orderDate
    } = order;

    const day = moment.tz(processedAt, timezone).startOf('day').format('YYYY-MM-DD'); // Grouping by day
    const totalProducts = lineItems.reduce((acc, item) => acc + item.quantity, 0);

    if (!summary[day]) {
      summary[day] = {};
    }

    if (!summary[day][locationId]) {
      summary[day][locationId] = {
        totalOrders: 0,
        totalProducts: 0,
        totalSubTotal: 0,
        totalCommission: 0,
      };
    }

    // Updating the totals for the day and location
    summary[day][locationId].totalOrders += 1;
    summary[day][locationId].totalProducts += totalProducts;
    summary[day][locationId].totalSubTotal += parseFloat(subTotal);
    summary[day][locationId].totalCommission += parseFloat(commission as string);
  });

  return summary;
};

export {
  getUsersSummary,
  getCommissionsByDate,
  mapWorkTimesByDate,
  getOrdersByPermissions,
  summarizeByLocation,
  summarizeByDayAndLocation,
};
